import {
  Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, Subscription, merge } from 'rxjs';
import { debounceTime, map, distinctUntilChanged } from 'rxjs/operators';

/** One row in the dropdown. `image` is optional, playlists have no thumbnail. */
export interface SearchSuggestion {
  label: string;
  image?: string;
}

@Component({
  selector: 'app-profile-search',
  templateUrl: './profile-search.component.html',
  styleUrls: ['./profile-search.component.scss']
})
export class ProfileSearchComponent implements OnInit, OnDestroy {

  /** Placeholder shown inside the box, already translated by the parent. */
  @Input() placeholder = '';
  /** Idle time before a keystroke turns into a request. */
  @Input() debounce = 400;
  /** Set by the parent while its request is in flight, drives the spinner. */
  @Input() searching = false;
  /** Paginator total for the current term, drives the result line. */
  @Input() resultCount: any = null;
  /**
   * Dropdown rows, built by the parent from the results it already fetched, so
   * the suggestions cost no extra request.
   */
  @Input() suggestions: SearchSuggestion[] = [];
  /** localStorage key for recent terms. Empty disables the recent list. */
  @Input() storageKey = '';

  /** Emits the trimmed term, and an empty string when the box is cleared. */
  @Output() search = new EventEmitter<string>();

  @ViewChild('searchInput') searchInput: ElementRef;

  control = new FormControl('');
  /** The term the parent is currently showing results for. */
  term = '';
  focused = false;
  open = false;
  /** Keyboard cursor in the dropdown, -1 means nothing highlighted. */
  activeIndex = -1;
  recent: string[] = [];
  /**
   * Built once whenever `recent` changes. Mapping inside the items getter would
   * hand *ngFor brand new objects on every change detection run, so the rows were
   * torn down between mousedown and mouseup and the click never landed.
   */
  recentItems: SearchSuggestion[] = [];

  /** Bypasses the debounce for Enter, picks and the clear button. */
  private immediate = new Subject<string>();
  /**
   * Set while picking a suggestion. pick() puts the caret back in the box, and
   * that programmatic focus would otherwise re-open the dropdown it just closed,
   * which looked like the first click doing nothing.
   */
  private suppressOpen = false;
  private sub: Subscription;
  private static readonly RECENT_LIMIT = 6;

  constructor(private host: ElementRef) { }

  ngOnInit() {
    this.setRecent(this.readRecent());

    // distinctUntilChanged sits after the merge so a debounced keystroke that
    // matches what Enter already sent does not fire a second request.
    this.sub = merge(
      this.control.valueChanges.pipe(debounceTime(this.debounce)),
      this.immediate
    ).pipe(
      map(value => (value === null || value === undefined) ? '' : value.toString().trim()),
      distinctUntilChanged()
    ).subscribe(value => {
      this.term = value;
      this.activeIndex = -1;
      this.search.emit(value);
    });
  }

  /** Recent terms when the box is empty, matched titles once something is typed. */
  get items(): SearchSuggestion[] {
    if (this.control.value) {
      return (this.suggestions || []).slice(0, 8);
    }
    return this.recentItems;
  }

  /** *ngFor identity, so a re-evaluated list does not re-create the rows. */
  trackByLabel(index: number, item: SearchSuggestion) {
    return item.label;
  }

  get showDropdown(): boolean {
    return this.open && this.items.length > 0;
  }

  get showingRecent(): boolean {
    return !this.control.value && this.recent.length > 0;
  }

  /** True once the parent has come back with a count for a non-empty term. */
  get showResultCount(): boolean {
    return this.term !== '' && !this.searching && this.resultCount !== null && this.resultCount > 0;
  }

  onFocus() {
    this.focused = true;
    this.openDropdown();
  }

  openDropdown() {
    if (this.suppressOpen) {
      return;
    }
    this.open = true;
  }

  onInput() {
    this.open = true;
    this.activeIndex = -1;
  }

  onArrow(delta: number, event: Event) {
    if (!this.showDropdown) {
      this.open = true;
      return;
    }
    event.preventDefault();
    const count = this.items.length;
    let next = this.activeIndex + delta;
    if (next < 0) {
      next = count - 1;
    } else if (next >= count) {
      next = 0;
    }
    this.activeIndex = next;
  }

  onEnter() {
    const list = this.items;
    if (this.showDropdown && this.activeIndex >= 0 && this.activeIndex < list.length) {
      this.pick(list[this.activeIndex]);
      return;
    }
    // Enter skips the remaining debounce instead of making the user wait.
    this.open = false;
    this.remember(this.control.value);
    this.immediate.next(this.control.value);
  }

  onEscape() {
    if (this.showDropdown) {
      this.open = false;
    } else if (this.control.value) {
      this.clear();
    } else {
      this.blurInput();
    }
  }

  pick(item: SearchSuggestion) {
    this.open = false;
    this.activeIndex = -1;
    this.control.setValue(item.label);
    this.remember(item.label);
    this.immediate.next(item.label);
    // focus() fires its event synchronously, so the flag only covers this call.
    this.suppressOpen = true;
    this.focusInput();
    this.suppressOpen = false;
  }

  clear() {
    this.control.setValue('');
    this.activeIndex = -1;
    this.immediate.next('');
    this.focusInput();
  }

  clearRecent(event: Event) {
    event.stopPropagation();
    this.setRecent([]);
    this.writeRecent();
  }

  /** Splits a label around the typed term so the template can bold the match. */
  parts(label: string): { text: string, hit: boolean }[] {
    const value = (this.control.value || '').toString().trim();
    const text = label || '';
    if (!value) {
      return [{ text: text, hit: false }];
    }
    const at = text.toLowerCase().indexOf(value.toLowerCase());
    if (at < 0) {
      return [{ text: text, hit: false }];
    }
    const out = [];
    if (at > 0) {
      out.push({ text: text.substr(0, at), hit: false });
    }
    out.push({ text: text.substr(at, value.length), hit: true });
    if (at + value.length < text.length) {
      out.push({ text: text.substr(at + value.length), hit: false });
    }
    return out;
  }

  focusInput() {
    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }

  blurInput() {
    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.blur();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.host.nativeElement.contains(event.target)) {
      this.open = false;
    }
  }

  private remember(value: any) {
    const term = (value || '').toString().trim();
    if (!term || !this.storageKey) {
      return;
    }
    this.setRecent([term].concat(this.recent.filter(r => r.toLowerCase() !== term.toLowerCase()))
      .slice(0, ProfileSearchComponent.RECENT_LIMIT));
    this.writeRecent();
  }

  private setRecent(terms: string[]) {
    this.recent = terms;
    this.recentItems = terms.map(label => ({ label: label }));
  }

  // localStorage can throw in private browsing, and a stale value can be
  // anything, so both ends are guarded and fall back to an empty list.
  private readRecent(): string[] {
    if (!this.storageKey) {
      return [];
    }
    try {
      const raw = localStorage.getItem(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(v => typeof v === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  private writeRecent() {
    if (!this.storageKey) {
      return;
    }
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.recent));
    } catch (e) {
      // Nothing to do, the recent list is a convenience only.
    }
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    this.immediate.complete();
  }
}
